// Rich "info page" editor for a Welcome Day resource — the same block model the
// Eng Tour uses (text / image / video / link), adapted to Welcome Day's image
// upload (uploadImage → /api/welcome-day) and styling. Content is an ordered
// stack of blocks; each block's reorder/remove controls sit in its corners, and
// link/video URL editors animate in on hover. Hover a divider to reveal a [+]
// that inserts a block there. Serializes back to the [[...]] token string.
import { useCallback, useEffect, useRef, useState } from 'react';
import { picUrl, uploadImage } from './placeImages.js';
import { parseBlocks, serializeBlocks, makeBlock, BLOCK_TYPES } from './richBody.js';
import { VideoEmbed } from './RichText.jsx';

const C = { blue: '#0060a8', gold: '#fcb40c', brown: '#431a0d', line: '#e0d6bf', cream: '#fffdf7', sand: '#f5efe1', ink: '#43382a' };

export default function RichBodyEditor({ value, onChange, uploadFolder = 'welcome_day/resources' }) {
  const [blocks, setBlocks] = useState(() => parseBlocks(value));
  // If the edited resource changes underneath us (value becomes a different
  // resource's body), re-parse. We compare against the last string we emitted.
  const lastStr = useRef(value || '');
  useEffect(() => {
    if ((value || '') !== lastStr.current) { setBlocks(parseBlocks(value)); lastStr.current = value || ''; }
  }, [value]);

  const commit = useCallback((next) => {
    setBlocks(next);
    const s = serializeBlocks(next);
    lastStr.current = s;
    onChange(s);
  }, [onChange]);

  const updateBlock = (i, patch) => commit(blocks.map((b, j) => (j === i ? { ...b, ...patch } : b)));
  const removeBlock = (i) => commit(blocks.filter((_, j) => j !== i));
  const insertAt = (i, type) => { const next = [...blocks]; next.splice(i, 0, makeBlock(type)); commit(next); };
  const moveBlock = (i, delta) => {
    const j = i + delta;
    if (j < 0 || j >= blocks.length) return;
    const next = [...blocks];
    [next[i], next[j]] = [next[j], next[i]];
    commit(next);
  };

  return (
    <div className="pf-field">
      <span className="pf-label">Details (info page — text, images, video, links)</span>
      <div style={{ background: C.cream, border: `1px solid ${C.line}`, borderRadius: 12, padding: 12 }}>
        {blocks.length === 0 && (
          <div style={{ color: '#9a8763', fontSize: 12, textAlign: 'center', padding: '6px 0' }}>
            Add text, an image, a video, or a link with the + below.
          </div>
        )}
        <InsertDivider onInsert={(t) => insertAt(0, t)} />
        {blocks.map((b, i) => (
          <div key={b._id}>
            <Block block={b} index={i} count={blocks.length} uploadFolder={uploadFolder}
              onChange={(patch) => updateBlock(i, patch)} onMove={(d) => moveBlock(i, d)} onRemove={() => removeBlock(i)} />
            <InsertDivider onInsert={(t) => insertAt(i + 1, t)} />
          </div>
        ))}
      </div>
    </div>
  );
}

// ── One block: text / image / video / link with corner controls ───────────────
function Block({ block, index, count, uploadFolder, onChange, onMove, onRemove }) {
  const [hover, setHover] = useState(false);
  const corner = (extra) => ({
    position: 'absolute', zIndex: 4, width: 26, height: 26, borderRadius: 7,
    background: '#fff', boxShadow: '0 2px 8px rgba(0,0,0,0.28)', cursor: 'pointer',
    fontSize: 12, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center',
    opacity: hover ? 1 : 0, transition: 'opacity 120ms ease', ...extra,
  });
  return (
    <div onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)} style={{ position: 'relative', margin: '6px 0' }}>
      {block.type === 'text' && (
        <AutoTextarea value={block.value} onChange={(v) => onChange({ value: v })} placeholder="Write a paragraph…"
          style={{ width: '100%', boxSizing: 'border-box', fontSize: 14, color: C.ink, lineHeight: 1.6, border: `1px solid ${C.line}`, borderRadius: 8, outline: 'none', background: '#fff', padding: '8px 10px', resize: 'none', fontFamily: 'inherit' }} />
      )}
      {block.type === 'image' && <ImageDrop src={block.src} height={block.height} uploadFolder={uploadFolder} onUploaded={(p) => onChange({ src: p })} onHeight={(h) => onChange({ height: h })} />}
      {block.type === 'video' && <VideoBlock url={block.url} autoplay={block.autoplay} hover={hover} onChange={onChange} />}
      {block.type === 'link' && <LinkBlock label={block.label} url={block.url} hover={hover} onChange={onChange} />}

      <button onClick={onRemove} title="Remove block" style={corner({ top: -8, left: -8, border: '1px solid #e0b4ad', color: '#c0392b' })}>✕</button>
      <button onClick={() => onMove(-1)} disabled={index === 0} title="Move up"
        style={corner({ top: -8, right: -8, border: `1px solid ${C.line}`, color: '#5b4d3b', opacity: hover ? (index === 0 ? 0.3 : 1) : 0, cursor: index === 0 ? 'default' : 'pointer' })}>↑</button>
      <button onClick={() => onMove(1)} disabled={index === count - 1} title="Move down"
        style={corner({ bottom: -8, right: -8, border: `1px solid ${C.line}`, color: '#5b4d3b', opacity: hover ? (index === count - 1 ? 0.3 : 1) : 0, cursor: index === count - 1 ? 'default' : 'pointer' })}>↓</button>
    </div>
  );
}

// Growing textarea (keeps the cursor stable while editing).
function AutoTextarea({ value, onChange, placeholder, style }) {
  const ref = useRef(null);
  const resize = () => { const el = ref.current; if (el) { el.style.height = 'auto'; el.style.height = `${el.scrollHeight}px`; } };
  useEffect(() => { resize(); }, [value]);
  return <textarea ref={ref} rows={1} value={value} placeholder={placeholder} onInput={resize} onChange={(e) => onChange(e.target.value)} style={style} />;
}

// ── Click-or-drag image dropzone (uploads to Welcome Day's endpoint) ──────────
function ImageDrop({ src, height, uploadFolder, onUploaded, onHeight }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const [err, setErr] = useState(null);
  const [resizing, setResizing] = useState(false);

  const handleFile = useCallback(async (file) => {
    if (!file || !file.type?.startsWith('image/')) { setErr("That's not an image file."); return; }
    setBusy(true); setErr(null);
    try { const path = await uploadImage(file, uploadFolder); onUploaded(path); }
    catch (e) { setErr(e?.message || 'Upload failed — try again.'); }
    finally { setBusy(false); }
  }, [uploadFolder, onUploaded]);

  const startResize = (e) => {
    e.preventDefault(); e.stopPropagation();
    const startY = e.clientY, startH = height || 200;
    setResizing(true);
    const move = (ev) => onHeight(Math.round(Math.max(60, Math.min(700, startH + (ev.clientY - startY)))));
    const up = () => { setResizing(false); window.removeEventListener('mousemove', move); window.removeEventListener('mouseup', up); };
    window.addEventListener('mousemove', move); window.addEventListener('mouseup', up);
  };

  return (
    <div
      onClick={() => { if (!resizing) inputRef.current?.click(); }}
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => { e.preventDefault(); setDrag(false); handleFile(e.dataTransfer.files?.[0]); }}
      style={{
        position: 'relative', cursor: resizing ? 'ns-resize' : 'pointer', borderRadius: 10, overflow: 'hidden',
        border: `2px dashed ${drag ? C.blue : (src ? 'transparent' : C.line)}`,
        background: src ? '#000' : (drag ? '#eaf6fc' : '#fbfaf7'),
        minHeight: src ? 0 : 96, display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
      {src
        ? <img src={picUrl(src)} alt="" style={{ width: '100%', height: height ? `${height}px` : 'auto', maxHeight: height ? 'none' : 240, objectFit: 'cover', display: 'block', opacity: busy ? 0.5 : 1 }} onError={(e) => { e.target.style.opacity = 0.3; }} />
        : <div style={{ textAlign: 'center', color: busy ? C.blue : '#a08f78', padding: 12 }}>
            <div style={{ fontSize: 22, marginBottom: 4 }}>{busy ? '⏳' : '🖼'}</div>
            <div style={{ fontSize: 12, fontWeight: 700 }}>{busy ? 'Uploading…' : 'Click or drag an image'}</div>
            {err && <div style={{ fontSize: 11, color: '#c0392b', marginTop: 4, maxWidth: 260 }}>{err}</div>}
          </div>}
      {src && (
        <div style={{ position: 'absolute', top: 8, right: 8, background: 'rgba(0,0,0,0.6)', color: '#fff', fontSize: 11, fontWeight: 700, padding: '3px 7px', borderRadius: 6, zIndex: 3 }}>
          {busy ? 'Uploading…' : 'Click to replace'}
        </div>
      )}
      {src && (
        <div onMouseDown={startResize} onClick={(e) => e.stopPropagation()} title="Drag to crop height (width locked)"
          style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 18, cursor: 'ns-resize', zIndex: 4, background: 'linear-gradient(to top, rgba(0,0,0,0.5), rgba(0,0,0,0))', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', paddingBottom: 4 }}>
          <div style={{ width: 44, height: 5, borderRadius: 3, background: 'rgba(255,255,255,0.9)' }} />
        </div>
      )}
      <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp" style={{ display: 'none' }}
        onChange={(e) => { handleFile(e.target.files?.[0]); e.target.value = ''; }} />
    </div>
  );
}

// Centred URL editor overlaid on a media block, so editing never shifts layout.
function UrlOverlay({ icon, title, value, placeholder, onChange, onClose, dim, text, onText }) {
  const hasText = onText !== undefined;
  const labelStyle = { fontSize: 9, fontWeight: 800, letterSpacing: '0.08em', color: '#a08f78', margin: '0 0 5px' };
  const inputStyle = { width: '100%', boxSizing: 'border-box', fontSize: 12, padding: '7px 9px', border: `1px solid ${C.line}`, borderRadius: 7, outline: 'none', background: '#fff' };
  return (
    <>
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, zIndex: 6, background: dim ? 'rgba(20,30,45,0.5)' : 'transparent', borderRadius: 12 }} />
      <div onClick={(e) => e.stopPropagation()} style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', zIndex: 7, width: 'min(86%, 300px)', background: '#fff', borderRadius: 10, padding: '10px 12px', boxShadow: '0 8px 26px rgba(0,0,0,0.4)', border: `1px solid ${C.line}` }}>
        {hasText && (
          <>
            <div style={labelStyle}>✎ BUTTON TEXT</div>
            <input autoFocus value={text} onChange={(e) => onText(e.target.value)} placeholder="Button label"
              onKeyDown={(e) => { if (e.key === 'Enter') onClose(); }} style={{ ...inputStyle, marginBottom: 9 }} />
          </>
        )}
        <div style={labelStyle}>{icon} {title}</div>
        <div style={{ display: 'flex', gap: 6 }}>
          <input autoFocus={!hasText} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
            onKeyDown={(e) => { if (e.key === 'Enter') onClose(); }} style={{ ...inputStyle, flex: 1, minWidth: 0 }} />
          <button onClick={onClose} title="Done" style={{ border: 'none', background: C.blue, color: '#fff', borderRadius: 7, padding: '0 12px', cursor: 'pointer', fontWeight: 800, fontSize: 13 }}>✓</button>
        </div>
      </div>
    </>
  );
}

function EditLinkButton({ label, onClick }) {
  return (
    <button onClick={onClick} title="Edit"
      style={{ position: 'absolute', top: 8, left: '50%', transform: 'translateX(-50%)', zIndex: 5, background: 'rgba(0,80,140,0.92)', color: '#fff', border: 'none', borderRadius: 999, padding: '5px 12px', fontSize: 11, fontWeight: 800, cursor: 'pointer', whiteSpace: 'nowrap', boxShadow: '0 2px 8px rgba(0,0,0,0.3)' }}>{label}</button>
  );
}

// Gold link button with inline-editable label; URL editor opens centred on top.
function LinkBlock({ label, url, hover, onChange }) {
  const [editing, setEditing] = useState(false);
  const open = editing || !url;
  return (
    <div style={{ position: 'relative' }}>
      <div style={{ background: C.gold, borderRadius: 11, padding: '10px 14px', display: 'flex', justifyContent: 'center', outline: hover ? `2px solid ${C.brown}` : '2px solid transparent', transition: 'outline-color 120ms' }}>
        <input value={label} onChange={(e) => onChange({ label: e.target.value })} placeholder="Button label"
          style={{ border: 'none', outline: 'none', background: 'transparent', textAlign: 'center', color: C.brown, fontWeight: 800, fontSize: 14, width: '100%' }} />
      </div>
      {hover && !open && <EditLinkButton label="🔗 Edit link" onClick={() => setEditing(true)} />}
      {open && (
        <UrlOverlay icon="🔗" title="LINK URL" value={url} placeholder="https://…"
          text={label} onText={(v) => onChange({ label: v })} onChange={(v) => onChange({ url: v })} onClose={() => setEditing(false)} dim={false} />
      )}
    </div>
  );
}

// Video embed/placeholder; URL editor opens centred on top, plus an auto-play toggle.
function VideoBlock({ url, autoplay, hover, onChange }) {
  const [editing, setEditing] = useState(false);
  const open = editing || !url;
  return (
    <div style={{ position: 'relative' }}>
      {url
        ? <div style={{ borderRadius: 12, overflow: 'hidden', background: '#000' }}><VideoEmbed url={url} /></div>
        : <div style={{ height: 110, display: 'flex', alignItems: 'center', justifyContent: 'center', border: `2px dashed ${C.line}`, borderRadius: 12, color: '#b6a892', fontSize: 12, fontWeight: 700 }}>🎬 Add a video</div>}
      {url && (
        <button onClick={() => onChange({ autoplay: !autoplay })} title="Auto-play this video (muted) when the popup opens"
          style={{ marginTop: 6, display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 11px', borderRadius: 999, border: `1.5px solid ${autoplay ? C.gold : C.line}`, background: autoplay ? C.gold : '#fff', color: autoplay ? C.brown : '#6b5d4b', fontSize: 12, fontWeight: 800, cursor: 'pointer' }}>
          {autoplay ? '▶ Auto-play: ON' : '▶ Auto-play: OFF'}
        </button>
      )}
      {hover && !open && <EditLinkButton label="🎬 Edit link" onClick={() => setEditing(true)} />}
      {open && (
        <UrlOverlay icon="🎬" title="VIDEO URL" value={url} placeholder="YouTube / mp4 URL"
          onChange={(v) => onChange({ url: v })} onClose={() => setEditing(false)} dim={!!url} />
      )}
    </div>
  );
}

// Divider with a hover-revealed [+] that inserts a media block at this position.
function InsertDivider({ onInsert }) {
  const [hover, setHover] = useState(false);
  const [menu, setMenu] = useState(false);
  const open = hover || menu;
  return (
    <div onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      style={{ position: 'relative', height: open ? 30 : 16, transition: 'height 120ms ease' }}>
      <div style={{ position: 'absolute', top: '50%', left: 0, right: 0, borderTop: `1px dashed ${open ? C.blue : C.line}`, transition: 'border-color 120ms' }} />
      <button onClick={() => setMenu((m) => !m)} title="Insert media here"
        style={{ position: 'absolute', top: '50%', left: '50%', zIndex: 16, transform: `translate(-50%,-50%) scale(${open ? 1 : 0.55})`, opacity: open ? 1 : 0.28, transition: 'transform 130ms ease, opacity 130ms ease', width: 24, height: 24, borderRadius: 7, border: 'none', cursor: 'pointer', background: C.blue, color: '#fff', fontSize: 16, fontWeight: 800, lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.25)' }}>+</button>
      {menu && (
        <>
          <div onClick={() => { setMenu(false); setHover(false); }} style={{ position: 'fixed', inset: 0, zIndex: 14 }} />
          <div style={{ position: 'absolute', top: 'calc(50% + 14px)', left: '50%', transform: 'translateX(-50%)', zIndex: 15, background: '#fff', borderRadius: 10, padding: 6, display: 'flex', gap: 4, boxShadow: '0 6px 20px rgba(0,0,0,0.25)', border: `1px solid ${C.line}` }}>
            {BLOCK_TYPES.map((t) => (
              <button key={t.type} onClick={() => { onInsert(t.type); setMenu(false); setHover(false); }}
                style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, padding: '6px 10px', border: 'none', background: 'transparent', cursor: 'pointer', borderRadius: 7, color: C.brown }}
                onMouseEnter={(e) => (e.currentTarget.style.background = C.sand)}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}>
                <span style={{ fontSize: 16 }}>{t.emoji}</span>
                <span style={{ fontSize: 10, fontWeight: 800 }}>Add {t.label}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
